import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import {
  IconButton, Tooltip, Avatar, ListItemAvatar, ListItemText, ListItemButton,
} from '@mui/material';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import BatteryCharging60Icon from '@mui/icons-material/BatteryCharging60';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20';
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { devicesActions } from '../store';
import {
  formatAlarm, formatBoolean, formatPercentage, formatStatus, getStatusColor,
} from '../common/util/formatter';
import { useTranslation } from '../common/components/LocalizationProvider';
//this must be here...
import { mapIconKey, mapIcons } from '../map/core/preloadImages';
import { useAdministrator } from '../common/util/permissions';
import EngineIcon from '../resources/images/data/engine.svg?react';
import GaugeMin from '../resources/images/data/gauge-min.svg?react';
import GaugeLow from '../resources/images/data/gauge-low.svg?react';
import GaugeHigh from '../resources/images/data/gauge-high.svg?react';
import GaugeMax from '../resources/images/data/gauge-max.svg?react';
import NoGPS from '../resources/images/data/no-gps.svg?react';
import { useAttributePreference } from '../common/util/preferences';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WifiIcon from '@mui/icons-material/Wifi';
import SVGIcon from "../common/components/SVGIcon";
import {formatSpeed} from "../common/util/formatter";


dayjs.extend(relativeTime);

const useStyles = makeStyles((theme) => ({
  icon: {
    width: '25px',
    height: '25px',
    filter: 'brightness(0) invert(1)',
  },
  group: {
    paddingLeft: 8,
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  batteryText: {
    fontSize: '0.75rem',
    fontWeight: 'normal',
    lineHeight: '0.875rem',
  },
  success: {
    fill: theme.palette.success.main,
    color: theme.palette.success.main,
  },
  warning: {
    color: theme.palette.warning.main,
    fill: theme.palette.warning.main,
  },
  error: {
    color: theme.palette.error.main,
    fill: theme.palette.error.main,
  },
  neutral: {
    color: theme.palette.neutral.main,
    fill: theme.palette.neutral.main,
  },
}));

const DeviceRow = ({ data, index, style }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const t = useTranslation();

  const admin = useAdministrator();

  const item = data[index];
  const position = useSelector((state) => state.session.positions[item.id]);
  const devicePrimary = useAttributePreference('devicePrimary', 'name');
  const deviceSecondary = useAttributePreference('deviceSecondary', '');
  const groups = useSelector((state) => state.groups.items);
  const drivers = useSelector((state) => state.drivers.items);
  const speedUnit = useAttributePreference('speedUnit');
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);


  const primaryText = () => {
    if (position && position.attributes.driverUniqueId && position.attributes.driverUniqueId !== ' ' && position.attributes.driverUniqueId !== ' ') {
      const driver = Object.values(drivers).find(g => g.uniqueId === position.attributes.driverUniqueId)?.name
          || position.attributes.driverUniqueId
      return `${item[devicePrimary]} • ${driver}`
    }
    return item[devicePrimary]
  }
  const secondaryText = () => {
    let status;
    if (item.status === 'online' || !item.lastUpdate) {
      status = formatStatus(item.status, t);
    } else {
      status = dayjs(item.lastUpdate).fromNow();
    }

    const extractCity = (address) => {
      if (!address) return '';
      const parts = address.split(',');
      return parts.length > 1 ? parts[parts.length - 2].trim() : '';
    };
    const city = position?.address ? extractCity(position.address) : '';

    return (
      <>
        {deviceSecondary && item[deviceSecondary] && `${item[deviceSecondary]} • `}
        <span className={classes[getStatusColor(item.status)]}>{city && `${city}, `}{status}</span>
        {item.groupId ? (<span className={classes.group}>{Object.values(groups).find(g => g.id === item.groupId)?.name}</span>) : ''}
      </>
    );
  };

  return (
    <div style={style}>
      <ListItemButton
        style={{paddingLeft: 8, paddingRight: 8}}
        key={item.id}
        onClick={() => dispatch(devicesActions.selectId(item.id))}
        disabled={!admin && item.disabled}
        selected={selectedDeviceId === item.id}
      >
        <ListItemAvatar>
            <SVGIcon device={item} position={position} />
        </ListItemAvatar>
        <ListItemText
          primary={primaryText()}
          primaryTypographyProps={{ noWrap: true }}
          secondary={secondaryText()}
          secondaryTypographyProps={{ noWrap: true }}
        />
        {position && (
          <>
            {position.attributes.hasOwnProperty('approximate') && (
              <Tooltip title={`${t('positionApproximate')}: ${formatBoolean(position.attributes.approximate, t)}`}>
                <IconButton size="small">
                  {position.attributes.approximate ? (
                      <WifiIcon width={20} height={20} className={classes.success} />
                  ) : (
                      <WifiIcon width={20} height={20} className={classes.neutral} />
                  )}
                </IconButton>
              </Tooltip>
           )}
            {position.attributes.hasOwnProperty('blocked') && (
              <Tooltip title={`${t('positionBlocked')}: ${formatBoolean(position.attributes.blocked, t)}`}>
                <IconButton size="small">
                  {position.attributes.blocked ? (
                      <LockIcon width={20} height={20} className={classes.error} />
                  ) : (
                      <LockOpenIcon width={20} height={20} className={classes.neutral} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {position.attributes.hasOwnProperty('alarm') && (
              <Tooltip title={`${t('eventAlarm')}: ${formatAlarm(position.attributes.alarm, t)}`}>
                <IconButton size="small">
                  <ErrorIcon fontSize="small" className={classes.error} />
                </IconButton>
              </Tooltip>
            )}
            {position.attributes.hasOwnProperty('ignition') && (
              <Tooltip title={`${t('positionIgnition')}: ${formatBoolean(position.attributes.ignition, t)}`}>
                <IconButton size="small">
                  {position.attributes.ignition ? (
                    <EngineIcon width={20} height={20} className={classes.success} />
                  ) : (
                    <EngineIcon width={20} height={20} className={classes.neutral} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {position.attributes.hasOwnProperty('batteryLevel') && (
              <Tooltip title={`${t('positionBatteryLevel')}: ${formatPercentage(position.attributes.batteryLevel)}`}>
                <IconButton size="small">
                  {(position.attributes.batteryLevel > 70 && (
                    position.attributes.charge
                      ? (<BatteryChargingFullIcon fontSize="small" className={classes.success} />)
                      : (<BatteryFullIcon fontSize="small" className={classes.success} />)
                  )) || (position.attributes.batteryLevel > 30 && (
                    position.attributes.charge
                      ? (<BatteryCharging60Icon fontSize="small" className={classes.warning} />)
                      : (<Battery60Icon fontSize="small" className={classes.warning} />)
                  )) || (
                    position.attributes.charge
                      ? (<BatteryCharging20Icon fontSize="small" className={classes.error} />)
                      : (<Battery20Icon fontSize="small" className={classes.error} />)
                  )}
                </IconButton>
              </Tooltip>
            )}
            {item.status === 'online' && (
                <Tooltip title={`${t('positionSpeed')}: ${formatSpeed(position.speed, speedUnit, t)}`}>
                    {(() => {
                      if (position.speed > 50) {
                        return <IconButton size="small"><GaugeMax className={classes.error} width={20} height={20} /></IconButton>;
                      } else if (position.speed > 25) {
                        return <IconButton size="small"><GaugeHigh className={classes.success} width={20} height={20} /></IconButton>;
                      } else if (position.speed > 0) {
                        return <IconButton size="small"><GaugeLow className={classes.success} width={20} height={20} /></IconButton>;
                      } else if (position.attributes.ignition) {
                        return <IconButton size="small"><GaugeMin className={classes.warning} width={20} height={20} /></IconButton>;
                      }
                    })()}
                </Tooltip>
            )}
            {position && !position.valid && (
                <Tooltip title={`${t('positionInvalid')}`}>
                  <IconButton size="small"><NoGPS className={classes.error} width={20} height={20}></NoGPS></IconButton>
                </Tooltip>
            )}
          </>
        )}
      </ListItemButton>
    </div>
  );
};

export default DeviceRow;
